import type { UserProfile } from '../contexts/AuthContext';
import { RepairStatus, type RepairRequest } from '../types/firestore';
import type { ReviewInvitation } from '../types/reviewInvitation';

export interface ReviewInvitationEligibilityInput {
  invitation: ReviewInvitation;
  request: RepairRequest | null;
  canonicalReviewExists: boolean;
  currentUserId: string | null | undefined;
  userProfile: Pick<UserProfile, 'role' | 'status'> | null | undefined;
  currentPathname: string;
  currentSearch: string;
  nowMs?: number;
}

export const timestampToMillis = (value: unknown): number | null => {
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isFinite(millis) ? millis : null;
  }
  if (!value || typeof value !== 'object') return null;

  const timestampLike = value as { toMillis?: unknown; seconds?: unknown; nanoseconds?: unknown };
  if (typeof timestampLike.toMillis === 'function') {
    try {
      const millis = Number((timestampLike.toMillis as () => unknown)());
      return Number.isFinite(millis) ? millis : null;
    } catch (_error) {
      return null;
    }
  }
  if (typeof timestampLike.seconds === 'number') {
    const millis =
      timestampLike.seconds * 1000 +
      (typeof timestampLike.nanoseconds === 'number' ? timestampLike.nanoseconds / 1_000_000 : 0);
    return Number.isFinite(millis) ? millis : null;
  }
  return null;
};

const normalizedString = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const isSameRequestReviewRoute = (requestId: string, pathname: string, search: string): boolean => {
  const routeMatch = pathname.match(/^\/app\/request\/([^/]+)$/);
  if (!routeMatch || routeMatch[1] !== requestId) return false;
  return new URLSearchParams(search).get('section') === 'review';
};

export const isReviewInvitationPromptable = ({
  invitation,
  request,
  canonicalReviewExists,
  currentUserId,
  userProfile,
  currentPathname,
  currentSearch,
  nowMs = Date.now(),
}: ReviewInvitationEligibilityInput): boolean => {
  const requestId = normalizedString(invitation.requestId);
  const clientId = normalizedString(invitation.clientId);
  const serviceProviderId = normalizedString(invitation.serviceProviderId);
  const createdAtMs = timestampToMillis(invitation.createdAt);
  const snoozedUntilMs =
    invitation.snoozedUntil === undefined || invitation.snoozedUntil === null
      ? null
      : timestampToMillis(invitation.snoozedUntil);

  if (
    !normalizedString(currentUserId) ||
    userProfile?.role !== 'client' ||
    userProfile.status !== 'active' ||
    !requestId ||
    invitation.id !== requestId ||
    invitation.schemaVersion !== 2 ||
    invitation.type !== 'review_invitation' ||
    invitation.status !== 'pending' ||
    clientId !== normalizedString(currentUserId) ||
    !serviceProviderId ||
    invitation.targetUrl !== `/app/request/${requestId}?section=review` ||
    invitation.occurrenceKind !== 'request_completed_review_invitation' ||
    invitation.occurrenceVersion !== 1 ||
    normalizedString(invitation.completionActorUserId) !== serviceProviderId ||
    invitation.completionActorRole !== 'service' ||
    createdAtMs === null ||
    (invitation.snoozedUntil !== undefined &&
      invitation.snoozedUntil !== null &&
      (snoozedUntilMs === null || snoozedUntilMs > nowMs)) ||
    !request ||
    request.clientId !== clientId ||
    request.serviceProviderId !== serviceProviderId ||
    request.status !== RepairStatus.COMPLETED ||
    canonicalReviewExists ||
    isSameRequestReviewRoute(requestId, currentPathname, currentSearch)
  ) {
    return false;
  }

  return true;
};

export const getReviewInvitationCreatedAtMs = (invitation: ReviewInvitation): number | null =>
  timestampToMillis(invitation.createdAt);
